// Firma: Excel şablonu ile toplu personel ekleme (önizleme -> onay -> kayıt + talebe ekleme)
import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { FileSpreadsheet, Download, Upload, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  new: { label: 'Eklenecek', cls: 'bg-emerald-100 text-emerald-700' },
  exists: { label: 'Kayıtlı – talebe eklenecek', cls: 'bg-sky-100 text-sky-700' },
  duplicate_file: { label: 'Eklenmeyecek', cls: 'bg-rose-100 text-rose-700' },
  invalid: { label: 'Eklenmeyecek', cls: 'bg-rose-100 text-rose-700' },
};

const downloadTemplate = async () => {
  try {
    const res = await axios.get(`${API}/employee-import/template`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = 'personel_sablonu.xlsx'; a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Şablon indirilemedi');
  }
};

export const ExcelImportDialog = ({ open, onOpenChange, companyId, onBehalfOf, onDone }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const close = () => { setRows(null); onOpenChange(false); };

  const preview = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/employee-import/preview`, fd);
      setRows(res.data.rows);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dosya okunamadı');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const confirm = async () => {
    setBusy(true);
    const created = [];
    const failed = [];
    for (const r of rows.filter(x => x.status === 'new')) {
      try {
        const res = await axios.post(`${API}/employees`, { company_id: companyId, name: r.name, surname: r.surname, tc_kimlik_no: r.tc_kimlik_no, nationality: r.nationality || 'tc', position: r.position, on_behalf_of: onBehalfOf?.trim() || null });
        created.push(res.data);
      } catch (err) {
        failed.push(`${r.name} ${r.surname}: ${err.response?.data?.detail || 'kaydedilemedi'}`);
      }
    }
    const existingIds = rows.filter(x => x.status === 'exists').map(x => x.existing_id);
    await onDone(created, existingIds);
    setBusy(false);
    if (failed.length) toast.error('Bazı personeller eklenemedi', { description: failed.join(' • '), duration: 10000 });
    toast.success(`${created.length} yeni personel kaydedildi, ${created.length + existingIds.length} personel talebe eklendi`);
    close();
  };

  const counts = rows ? Object.fromEntries(Object.keys(STATUS).map(k => [k, rows.filter(r => r.status === k).length])) : {};
  const addable = rows ? counts.new + counts.exists : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="excel-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" />Excel ile Personel Ekle</DialogTitle>
        </DialogHeader>
        {!rows ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-slate-50 p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700">1. Şablonu indirin ve doldurun</p>
              <p className="text-xs text-slate-500">Sütunlar: Ad, Soyad, T.C. Kimlik No (11 hane; yabancıda pasaport no), Görevi, Uyruk (T.C. / Yabancı, boş = T.C.). Örnek satırı silip kendi personellerinizi yazın.</p>
              <Button type="button" size="sm" onClick={downloadTemplate} className="bg-red-600 text-white hover:bg-red-700" data-testid="excel-template-download"><Download className="h-4 w-4 mr-1" />Excel Şablonunu İndir</Button>
            </div>
            <div className="rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-6 text-center space-y-2">
              <p className="text-sm font-medium text-slate-700">2. Doldurduğunuz Excel dosyasını yükleyin</p>
              <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => preview(e.target.files?.[0])} data-testid="excel-upload-input" />
              <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="excel-upload-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Excel Dosyası Seç
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 text-xs" data-testid="excel-preview-summary">
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Yeni kayıt: {counts.new}</Badge>
              <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Zaten kayıtlı: {counts.exists}</Badge>
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Aynı T.C. tekrar: {counts.duplicate_file}</Badge>
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Hatalı: {counts.invalid}</Badge>
            </div>
            {counts.duplicate_file > 0 && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="excel-duplicate-warning">Dosyada aynı T.C. Kimlik No ile birden fazla satır var. Tekrar eden satırlar kaydedilmeyecek.</p>}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-600">
                  <th className="px-2 py-2">Satır</th><th className="px-2">Ad Soyad</th><th className="px-2">T.C. Kimlik No</th><th className="px-2">Görevi</th><th className="px-2">Durum</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.row} className={`border-t ${['duplicate_file', 'invalid'].includes(r.status) ? 'bg-rose-50/60' : ''}`} data-testid={`excel-row-${r.row}`}>
                      <td className="px-2 py-1.5 text-slate-500">{r.row}</td>
                      <td className="px-2 font-medium">{r.name} {r.surname}</td>
                      <td className="px-2 font-mono">{r.tc_kimlik_no || '-'}{r.nationality === 'yabanci' && <span className="ml-1 rounded bg-indigo-100 px-1 text-[9px] font-bold text-indigo-700">PASAPORT</span>}</td>
                      <td className="px-2">{r.position || '-'}</td>
                      <td className="px-2"><Badge className={`${STATUS[r.status].cls} hover:${STATUS[r.status].cls} text-[10px]`}>{STATUS[r.status].label}</Badge>{r.reason && <p className="text-[10px] text-slate-500 mt-0.5">{r.reason}</p>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <DialogFooter className="gap-2">
          {rows && <Button variant="outline" onClick={() => setRows(null)} disabled={busy} data-testid="excel-preview-back">Başka Dosya Yükle</Button>}
          <Button variant="outline" onClick={close} disabled={busy}>İptal</Button>
          {rows && (
            <Button onClick={confirm} disabled={busy || addable === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="excel-import-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Onayla ve Talebe Ekle ({addable})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
